import * as React from "react";
import gql from "graphql-tag";
import { graphql, compose } from "react-apollo";
import * as FontAwesome from "react-fontawesome";

import { groupAuth } from "app/utils/authGroups";
import handleMutationError from "app/utils/handleMutationError";

import Dialog from "material-ui/Dialog";
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  TextField,
  Typography,
  withStyles,
  createStyles
} from "@material-ui/core";
import PlusIcon from "@material-ui/icons/Add";

const hasAccessToWalletAdmin = groupAuth.hasAccessGroup(["Wallet/Admin"]);

type Props = {
  name: string;
  type: string;
  classes: any;
  wallets: any;
  addWallet: Function;
  clearStates: Function;
};

type State =
  | {
      modalOpen: boolean;
      name: string;
      displayName: string;
      submitted: boolean;
      modalButtonText: string;
      modalTextFieldDisabled: boolean;
    }
  | any;

const styles = createStyles({
  button: {
    background: "#4c5a86",
    border: "0 none",
    borderRadius: "4px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "0.9em",
    fontWeight: 100,
    lineHeight: "1.5em",
    margin: "0 5px",
    opacity: 1,
    outline: "none",
    padding: "5px 15px",
    textAlign: "center",
    textTransform: "capitalize",
    transition: "all 60ms ease -in -out",
    webkitTransition: "all 60ms ease-in -out",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#4c5a86",
      opacity: 0.9
    },
    "&:disabled": {
      backgroundColor: "#7d8088",
      color: "#e6e6e6"
    }
  },
  formControlLabel: {
    marginRight: "5px"
  },
  icon: {
    marginLeft: "5px"
  },
  iconButton: {
    fontSize: "1.2em"
  },
  label: {
    paddingBottom: "5px"
  },
  plusIcon: {
    fontSize: "0.9em",
    fontWeight: 100
  }
});

class WalletUpdateWallet extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      modalOpen: false,
      name: "",
      displayName: "",
      submitted: false,
      modalButtonText: "Add new Wallet",
      modalTextFieldDisabled: false
    };
  }

  handleFormSubmit = (e: any): void => {
    e.preventDefault();
    const { name, displayName, submitted } = this.state;
    if (!submitted) {
      this.setState({ submitted: true });

      this.props
        .addWallet({
          variables: {
            name,
            display_name: displayName
          }
        })
        .then(() => {
          this.setState({
            modalOpen: false
          });
        })
        .catch(error => {
          handleMutationError(error);
          this.setState({
            modalOpen: false
          });
        });
    }
  };

  handleInputChange = (name, value): void => {
    this.setState({ [name]: value });
  };

  handleModalState = (option: boolean, name = ""): void => {
    if (option) {
      this.setState({
        name,
        displayName: "",
        submitted: false,
        modalButtonText: name ? `Update ${name}` : "Add new Wallet",
        modalTextFieldDisabled: name ? true : false
      });
    }
    this.setState({ modalOpen: option });
  };

  render() {
    const { classes } = this.props;
    const {
      displayName,
      name,
      modalButtonText,
      modalTextFieldDisabled,
      modalOpen
    } = this.state;

    return (
      <>
        {this.props.type === "add" ? (
          <Button
            color="primary"
            className={classes.button}
            onClick={() => this.handleModalState(true)}
          >
            {`Add wallet `}
            <Icon className={classes.icon}>
              <PlusIcon className={classes.plusIcon} />
            </Icon>
          </Button>
        ) : (
          <IconButton
            className={classes.iconButton}
            onClick={() => this.handleModalState(true, this.props.name)}
            disabled={!hasAccessToWalletAdmin}
          >
            <FontAwesome
              className="BOWalletSubMenuTextWithEditButton"
              name="pencil"
            />
          </IconButton>
        )}

        <Dialog
          title={this.props.type === "add" ? `Add ${name}` : `Edit ${name}`}
          modal={false}
          open={modalOpen}
          onRequestClose={() => this.handleModalState(false)}
          actions={[]}
        >
          <FormControl fullWidth={true} style={{ alignItems: "flex-start" }}>
            <FormControlLabel
              label={
                <Typography className={classes.formControlLabel}>
                  Name:
                </Typography>
              }
              labelPlacement="start"
              className={classes.label}
              control={
                <TextField
                  name="name"
                  type="text"
                  value={name}
                  onChange={e =>
                    this.handleInputChange(e.target.name, e.target.value.trim())
                  }
                  disabled={modalTextFieldDisabled}
                />
              }
            />

            <FormControlLabel
              label={
                <Typography className={classes.formControlLabel}>
                  Display name:
                </Typography>
              }
              labelPlacement="start"
              className={classes.label}
              control={
                <TextField
                  name="displayName"
                  type="text"
                  value={displayName}
                  onChange={e =>
                    this.handleInputChange(e.target.name, e.target.value)
                  }
                />
              }
            />

            <Grid container justify="flex-end">
              <Button
                className={classes.button}
                disabled={name && displayName ? false : true}
                onClick={this.handleFormSubmit}
              >
                {modalButtonText}
              </Button>
            </Grid>
          </FormControl>
        </Dialog>
      </>
    );
  }
}

const addWalletMutation = graphql(
  gql`
    mutation addWallet($name: String!, $display_name: String!) {
      wallet_create_or_update_wallet(name: $name, display_name: $display_name) {
        name
      }
    }
  `,
  { name: "addWallet" }
);

export default compose<any>(addWalletMutation)(
  withStyles(styles)(WalletUpdateWallet)
);
